import * as React from "react";

import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Button, FormGroup, InputGroup, FormControl } from "react-bootstrap";
import { AmountChooserInfoPanel } from "amountchooser";
import { PriceAndPromotionInfo } from "shared/src/types/PriceAndPromotionInfo";
import { getPositionDisplayPrice } from "../../utils/PriceDisplayFramework";
import { InternalDisplayConfig } from "shared/src/types/ArticleCardProps";
import { isNumber } from "shared/src/utils/util";

type AmountChooserProps = {
    onChange: (amount: number | undefined | null) => void;
    onEnter: (amount: number | undefined | null) => void;
    priceAndPromotionInfo: PriceAndPromotionInfo | null;
    internalDisplayConfig: InternalDisplayConfig | null;
    amountInBasket: number | undefined | null;
    isWeightArticle: boolean;
    weightPerPiece: {
        unit: string | undefined;
        value: number | undefined;
    } | null;
    disabled: boolean;
    tabIndex?: number;
    amount: number | undefined | null;
    showWeightInformation: boolean;
};

type State = {
    showInfoPopover: boolean;
};

export class AmountChooser extends React.Component<AmountChooserProps, State> {
    static contextTypes = {
        intl: PropTypes.object.isRequired
    };

    state: State = { showInfoPopover: false };

    noWeightPerPiece() {
        return !this.props.weightPerPiece || !this.props.weightPerPiece?.unit || !this.props.weightPerPiece?.value;
    }

    parseAmount(amount: string) {
        if (this.props.isWeightArticle && this.noWeightPerPiece()) {
            return parseFloat(amount.replace(",", ".")) || 0.0;
        }
        return parseInt(amount, 10) || 0;
    }

    renderInfoPanel() {
        if (!isNumber(this.props.amountInBasket) || this.noWeightPerPiece()) {
            return null;
        }
        const totalPrice = getPositionDisplayPrice(this.props.priceAndPromotionInfo, this.props.internalDisplayConfig);
        return (
            <div className="sd-amount-chooser--absoluteposition">
                <AmountChooserInfoPanel
                    timeoutStandard={5000}
                    totalWeight={this.props.amountInBasket}
                    weightPerPieceValue={(this.props.weightPerPiece || {}).value}
                    unitOfMeasurement={(this.props.weightPerPiece || {}).unit}
                    totalPrice={totalPrice}
                    currency={this.props.priceAndPromotionInfo?.currency}
                    showPermanently={this.state.showInfoPopover || this.props.showWeightInformation}
                />
            </div>
        );
    }

    handleAmountChange(event: React.SyntheticEvent<any>) {
        event.preventDefault();
        const value = (event.target as any).value;
        if (value === "") {
            this.props.onChange(null);
        } else {
            this.props.onChange(Math.max(this.parseAmount((event.target as any).value), 0));
        }
    }

    handleItemAmountIncrease() {
        this.props.onChange((this.props.amount || 0) + 1);
    }

    handleItemAmountDecrease() {
        if (this.props.amount && this.props.amount >= 1) {
            this.props.onChange(this.props.amount - 1);
        }
    }

    showInfoPopover(event: any) {
        this.setState({
            showInfoPopover: true
        });
        event.target.select(); // Preselect the input value, so that typing resets the old one.
    }

    resetState() {
        this.setState({
            showInfoPopover: false
        });
        if (!this.props.amount) {
            this.props.onChange(1);
        }
    }

    handleKeyPress(event: any) {
        if (event.key === "Enter") {
            this.props.onEnter(undefined);
        }
    }

    render() {
        const displayAsWeightedArticle = this.props.isWeightArticle && !this.props.weightPerPiece;
        const weightAddon = displayAsWeightedArticle ? (
            <span className="form-control-addon">
                <FormattedMessage id="EX_ARTICLE_KG" />
            </span>
        ) : null;
        const touch = this.props.internalDisplayConfig?.isTouchScreen;
        return (
            <div
                className={
                    "sd-amount-chooser--actions-amount " +
                    (this.props.internalDisplayConfig?.isTouchScreen ? "touch-optimized " : "")
                }>
                <div className={"sd-amount-chooser"}>
                    <FormGroup className={weightAddon ? "has-addon" : ""}>
                        <InputGroup>
                            {touch && (
                                <InputGroup.Button>
                                    <Button
                                        bsStyle="link"
                                        className="sd-amount-chooser-decrease btn-square"
                                        aria-label={this.context.intl.formatMessage({
                                            id: "SD_DECREASE_BASKET_BUTTON_ARIA_LABEL"
                                        })}
                                        onClick={() => this.handleItemAmountDecrease()}>
                                        <span className="icon-minus icon-lg" />
                                    </Button>
                                </InputGroup.Button>
                            )}
                            <FormControl
                                value={this.props.amount!}
                                type="number"
                                disabled={this.props.disabled}
                                tabIndex={this.props.tabIndex}
                                onFocus={(event) => this.showInfoPopover(event)}
                                onBlur={() => this.resetState()}
                                onKeyPress={(event) => this.handleKeyPress(event)}
                                onChange={(event) => this.handleAmountChange(event)}
                                aria-label={this.context.intl.formatMessage({
                                    id: "SD_BASKET_AMOUNT_INPUT_ARIA_LABEL"
                                })}
                            />
                            {weightAddon}
                            {touch && (
                                <InputGroup.Button>
                                    <Button
                                        bsStyle="link"
                                        className="sd-amount-chooser-increase btn-square"
                                        aria-label={this.context.intl.formatMessage({
                                            id: "SD_INCREASE_BASKET_BUTTON_ARIA_LABEL"
                                        })}
                                        onClick={() => this.handleItemAmountIncrease()}>
                                        <span className="icon-plus icon-lg" />
                                    </Button>
                                </InputGroup.Button>
                            )}
                        </InputGroup>
                    </FormGroup>
                </div>
                {this.props.weightPerPiece && this.renderInfoPanel()}
            </div>
        );
    }
}
